import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import axios from "axios"
import { API_BASE } from "../../app/const"
import { fireauth } from "../auth/AuthSlice"
import { RootState } from "../../app/store"
export interface ApiKeyRestrictionsTarget {
  service: string[]
}
export interface BrowserKeyRestrictions {
  allowedReferrers: string[]
}
export interface ApiKeyRestrictions {
  apiTargets: ApiKeyRestrictionsTarget[]
  browserKeyRestrictions: BrowserKeyRestrictions
}
export interface GcloudTokenObj {
  gcloudApiId: number
  createTime: string
  createdFor: string
  displayName: string
  etag: string
  keyString: string
  name: string
  restrictions: ApiKeyRestrictions
  uid: string
  updateTime: string
}

export interface GcloudTokenState {
  status: 'idle' | 'loading' | 'failed'
  visible: boolean
  token: GcloudTokenObj
  tokens: GcloudTokenObj[]
}

const initialState: GcloudTokenState = {
  status: 'idle',
  visible: false,
  token: {
    gcloudApiId: 0,
    createTime: '',
    createdFor: '',
    displayName: '',
    etag: '',
    keyString: '',
    name: '',
    restrictions: {
      apiTargets: [],
      browserKeyRestrictions: {
        allowedReferrers: []
      }
    },
    uid: '',
    updateTime: '',
  },
  tokens: []
}

export const fetchMyGcloudTokensAsync = createAsyncThunk(
  'gcloud/fetchMyGcloudTokensAsync', async (args: any, { getState }) => {
    const state = getState() as RootState
    if (state) {
      const response = await axios.get(`${API_BASE}apps/api/gcloud/keys/my`, {
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
          "Authorization": `Bearer ${await fireauth.currentUser?.getIdToken()}`
        }
      })
      return response.data
    }
  }
)

export const fetchAllGcloudTokensAsync = createAsyncThunk(
  'gcloud/fetchAllGcloudTokensAsync', async (args: any, { getState }) => {
    const state = getState() as RootState
    if (state) {
      const response = await axios.get(`${API_BASE}apps/api/gcloud/keys`, {
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
          "Authorization": `Bearer ${await fireauth.currentUser?.getIdToken()}`
        }
      })
      return response.data
    }
  }
)

export const genGcloudTokenAsync = createAsyncThunk(
  'gcloud/genGcloudTokensAsync', async ({ allowedReferrers }: { allowedReferrers: string[] }, { getState }) => {
    const state = getState() as RootState
    if (state) {
      const response = await axios.post(`${API_BASE}apps/api/gcloud/keys`, { allowedReferrers }, {
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
          "Authorization": `Bearer ${await fireauth.currentUser?.getIdToken()}`
        }
      })
      return response.data
    }
  }
)

export const deleteGcloudTokenAsync = createAsyncThunk(
  'admin/deleteGcloudTokensAsync', async ({ gCloudKeyName }: { gCloudKeyName: string }, { getState }) => {
    const state = getState() as RootState
    if (state) {
      const response = await axios.delete(`${API_BASE}apps/api/gcloud/keys/${gCloudKeyName}`, {
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
          "Authorization": `Bearer ${await fireauth.currentUser?.getIdToken()}`
        }
      })
      return response.data
    }
  }
)

export const gcloudTokenSlice = createSlice({
  name: 'api-token',
  initialState,
  reducers: {
    resetGcloudToken: (state) => {
      state.status = 'idle'
      state.token = initialState.token
    },
    pickGcloudToken: (state, action) => {
      state.status = 'idle'
      state.token = action.payload
    },
    hideGcloudToken: (state) => {
      state.status = 'idle'
      state.visible = false
    }
  },
  extraReducers: (builder) => {
    builder.
      addCase(fetchMyGcloudTokensAsync.pending, (state) => {
        state.status = 'loading'
      }).addCase(fetchMyGcloudTokensAsync.fulfilled, (state, action) => {
        state.status = 'idle'
        state.tokens = action.payload
      }).addCase(fetchMyGcloudTokensAsync.rejected, (state, action) => {
        state.status = 'failed'
        state.tokens = []
      }).addCase(genGcloudTokenAsync.pending, (state) => {
        state.status = 'loading'
      }).addCase(genGcloudTokenAsync.fulfilled, (state, action) => {
        state.status = 'idle'
        state.token = action.payload
        state.visible = true
        state.tokens = [...state.tokens, action.payload]
      }).addCase(genGcloudTokenAsync.rejected, (state, action) => {
        state.status = 'failed'
        state.token = initialState.token
      }).addCase(deleteGcloudTokenAsync.pending, (state) => {
        state.status = 'loading'
      }).addCase(deleteGcloudTokenAsync.fulfilled, (state, action) => {
        state.status = 'idle'
        state.tokens = state.tokens.filter((token) => token.gcloudApiId !== action.payload.gcloudApiId)
      }).addCase(deleteGcloudTokenAsync.rejected, (state, action) => {
        state.status = 'failed'
      })
  }
})

export const { resetGcloudToken, pickGcloudToken, hideGcloudToken } = gcloudTokenSlice.actions
export const selectMyGcloudTokens = (state: RootState) => state.gcloudToken.tokens
export const selectGcloudToken = (state: RootState) => state.gcloudToken.token
export const selectGcloudTokenVisible = (state: RootState) => state.gcloudToken.visible
export const selectGcloudTokenStatus = (state: RootState) => state.gcloudToken.status
export default gcloudTokenSlice.reducer
